import { create } from 'zustand';
import { createTreeStoreBackendApi } from './createTreeStoreBackendApi';
import { log } from '@/lib/utils/logger';
import { formatErrorMessage } from '@/lib/utils/apiErrors';
import { TreeNode, TreeState, NodeType, TreeContextMenu, NodeDataType, NodeDataTypeMap } from '@/types/tree';
import { IBackend } from '@/api/BackendApi';
import { TreeNodeFactory } from '@/lib/tree/treeNodeFactory';
import { generateNodeId, extractEntityIdFromDataId } from '@/lib/tree/treeUtils';
import { applyCanonicalDataBrowseRootNodeMapping, DATA_ROUTE_ROOT_MODE_KEY } from '@/lib/tree/dataRouteTreeConstants';
import { useBackendStore } from '@/stores/backendStore';
import {
  UserSettingsDto,
  FileMetaDataDto,
  NoteDto,
  SampleRequestDto,
  SampleDto,
  SampleActivityDto,
  ProjectActivityDto,
  TheoryActivityDto,
  DataPackageDto,
  CommunicationDto,
  ShippingDto,
  PublicationDto,
  ProjectDto,
} from '@/api/ApiClient';
import { ProjectUIModel, toProjectUIModel } from '@/lib/models/projectUIModel';
import { useAuthStore } from '@/stores/authStore';
import { TreeBackendApi } from '@/types/tree/props';

/**
 * Root entity for the tree: use preloaded DTO from route resolution when present, else fetch.
 * Project roots from `/data/{id}` arrive as `ProjectDto` and are normalized like `getNode` does.
 */
async function loadTreeRootData(
  storeRootType: NodeType,
  logicalRootType: NodeType,
  rootId: string,
  preloadedRoot: NodeDataType | undefined,
  api: TreeBackendApi
): Promise<NodeDataType> {
  if (!preloadedRoot) {
    return api.getNode(storeRootType, rootId);
  }

  if (logicalRootType === 'project' || logicalRootType === 'projectInData') {
    return toProjectUIModel(preloadedRoot as ProjectDto);
  }
  if (logicalRootType === 'dataPackage') {
    return preloadedRoot as DataPackageDto;
  }
  if (logicalRootType === 'publication' || logicalRootType === 'publicationInData') {
    return preloadedRoot as PublicationDto;
  }

  return api.getNode(storeRootType, rootId);
}

/**
 * Tree store using Zustand
 * Manages the state of the tree structure including:
 * - Node data and hierarchy
 * - Lazy loading of children, notes, and files
 * - Node modification tracking
 * - UI state (expanded/selected nodes)
 *
 * @remarks
 * This store implements a tree data structure where nodes can be:
 * - Lazy loaded (children, notes, files)
 * - Modified and tracked for changes
 * - Expanded/collapsed in the UI
 * - Selected for operations
 *
 * The store uses a Map for O(1) node lookups and Sets for tracking various states.
 */
export const useTreeStore = create<TreeState>((set, get) => {
  // Debug helper to log store state
  const logStoreState = (action: string, details?: any) => {
    log.debug(`[TreeStore] ${action}:`, () => {
      const state = get();
      return {
        action,
        details,
        state: {
          nodes: Array.from(state.nodes.entries()),
          loadedNodes: Array.from(state.loadedNodes),
          expandedNodes: Array.from(state.expandedNodes),
          selectedNode: state.selectedNode?.nodeId,
          modifiedData: Array.from(state.modifiedData),
          data: Array.from(state.data.entries()),
        },
      };
    });
  };

  // Activity types that need label refresh and parent resort on sort-relevant edits.
  const ACTIVITY_NODE_TYPES = new Set<NodeType>(['sampleActivity', 'projectActivity', 'theoryActivity']);

  const getSortFieldsForNodeType = (nodeType: NodeType): string[] => {
    switch (nodeType) {
      case 'sampleActivity':
        return ['date', 'creationDate', 'category', 'type', 'instrument', 'characterizationTechnique', 'technique'];
      case 'projectActivity':
        return ['date', 'creationDate', 'type'];
      case 'theoryActivity':
        return ['date', 'creationDate'];
      default:
        return [];
    }
  };

  const computeChangedFields = (originalData: Record<string, unknown>, updatedData: Record<string, unknown>) => {
    const changed: Record<string, unknown> = {};
    const keys = new Set([...Object.keys(originalData), ...Object.keys(updatedData)]);
    for (const key of keys) {
      if (originalData[key] !== updatedData[key]) {
        changed[key] = updatedData[key];
      }
    }
    return changed;
  };

  // True when partial/full changes include fields that affect sibling order for this node type.
  const isSortFieldModified = (
    nodeType: NodeType,
    originalData: any,
    changedFields: Record<string, unknown>
  ): boolean => {
    const sortFields = getSortFieldsForNodeType(nodeType);
    for (const field of sortFields) {
      if (Object.prototype.hasOwnProperty.call(changedFields, field) && originalData[field] !== changedFields[field]) {
        return true;
      }
    }
    return false;
  };

  // Detail panels pass dataId; nodeId usually matches but suffixes can differ when data is shared.
  const resolveNode = (nodeIdOrDataId: string): TreeNode | undefined => {
    const { nodes } = get();
    const byNodeId = nodes.get(nodeIdOrDataId);
    if (byNodeId) {
      return byNodeId;
    }
    for (const node of nodes.values()) {
      if (node.dataId === nodeIdOrDataId) {
        return node;
      }
    }
    return undefined;
  };

  const resortParentChildren = (parentId: string) => {
    const { nodes, factory, setNode, getData } = get();
    if (!factory) {
      return;
    }
    const parent = nodes.get(parentId);
    if (!parent?.children) {
      return;
    }
    const childNodes = parent.children.map((childId) => nodes.get(childId)).filter(Boolean) as TreeNode[];
    const sortedChildIds = factory.sort(childNodes, getData).map((child) => child.nodeId);
    if (sortedChildIds.join(',') === parent.children.join(',')) {
      return;
    }
    setNode({ ...parent, children: sortedChildIds });
  };

  // Refresh denormalized tree labels and re-sort siblings after entity data changes.
  const syncTreeAfterDataChange = (node: TreeNode, originalData: any, changedFields: Record<string, unknown>) => {
    const { setNode } = get();
    if (ACTIVITY_NODE_TYPES.has(node.type) || node.type === 'sample') {
      const latestNode = get().nodes.get(node.nodeId) ?? node;
      setNode(latestNode);
    }
    if (
      ACTIVITY_NODE_TYPES.has(node.type) &&
      node.parentId &&
      isSortFieldModified(node.type, originalData, changedFields)
    ) {
      resortParentChildren(node.parentId);
    }
  };

  return {
    // Factory instance
    factory: null,

    // Node metadata (tree structure)
    nodes: new Map<string, TreeNode>(),

    // Entity data (shared across nodes)
    data: new Map<string, any>(),
    files: new Map<string, FileMetaDataDto[]>(),
    notes: new Map<string, NoteDto[]>(),
    sampleRequests: new Map<number, SampleRequestDto[]>(), // projectId -> SampleRequestDto[]

    // Loading state (moved from TreeNode for performance)
    notesLoaded: new Set<string>(),
    filesLoaded: new Set<string>(),
    childrenLoaded: new Set<string>(),

    // Modification state (moved from TreeNode for correctness)
    modifiedData: new Set<string>(),

    // UI state
    expandedNodes: new Set<string>(),
    selectedNode: null,
    loadedNodes: new Set<string>(),
    loading: new Map<string, boolean>(),
    contextMenu: null,

    setFactory: (userSettings: UserSettingsDto) => {
      const newFactory = new TreeNodeFactory(userSettings);
      set({ factory: newFactory });
    },

    setNode: (node) => {
      log.debug('[TreeStore] Setting node:', () => ({ nodeId: node.nodeId, type: node.type }));
      set((state) => {
        const newNodes = new Map(state.nodes);
        // Always recalculate label to ensure we don't miss changes
        const nodeData = state.data.get(node.dataId);
        const display =
          state.factory && nodeData
            ? TreeNodeFactory.getNodeDisplayData(node.type, nodeData, state.factory.userSettings)
            : TreeNodeFactory.getNodeDisplayData(node.type, nodeData || {});
        newNodes.set(node.nodeId, TreeNodeFactory.withNodeDisplayData(node, display));

        log.debug('[TreeStore] setNode - Before update:', {
          currentNodeCount: state.nodes.size,
          newNodeCount: newNodes.size,
          nodeId: node.nodeId,
        });

        // If the updated node is currently selected, update selectedNode as well
        if (state.selectedNode && state.selectedNode.nodeId === node.nodeId) {
          log.debug('[TreeStore] setNode - Updating selected node as well');
          return {
            nodes: newNodes,
            selectedNode: TreeNodeFactory.withNodeDisplayData(node, display),
          };
        }
        log.debug('[TreeStore] setNode - Returning new nodes map');
        return { nodes: newNodes };
      });
      logStoreState('setNode', { node });
    },

    setNodes: (nodes: TreeNode[]) => {
      log.debug('[TreeStore] Setting multiple nodes:', () => ({ count: nodes.length, nodes }));
      set((state) => {
        const newNodes = new Map(state.nodes);
        nodes.forEach((node) => {
          newNodes.set(node.nodeId, node);
        });
        return { nodes: newNodes };
      });
      logStoreState('setNodes', { nodes });
    },

    setExpandedNodes: (nodes: Set<string>) => {
      log.debug('[TreeStore] Setting expanded nodes:', () => ({ nodes: Array.from(nodes) }));
      set({ expandedNodes: nodes });
      logStoreState('setExpandedNodes', () => ({ nodes: Array.from(nodes) }));
    },

    setSelectedNode: (nodeId: string | null) => {
      log.debug('[TreeStore] Setting selected node:', () => ({ nodeId }));
      set((state) => {
        const node = nodeId ? state.nodes.get(nodeId) : null;
        return { selectedNode: node };
      });
      logStoreState('setSelectedNode', { nodeId });
    },

    setLoadedNode: (nodeId: string) => {
      log.debug('[TreeStore] Setting loaded node:', () => ({ nodeId }));
      set((state) => {
        const newLoadedNodes = new Set(state.loadedNodes);
        newLoadedNodes.add(nodeId);
        return { loadedNodes: newLoadedNodes };
      });
      logStoreState('setLoadedNode', { nodeId });
    },

    setLoading: (key: string, isLoading: boolean) => {
      set((state) => {
        const newLoading = new Map(state.loading);
        if (isLoading) {
          newLoading.set(key, true);
        } else {
          newLoading.delete(key);
        }
        return { loading: newLoading };
      });
    },

    setContextMenu: (menu: TreeContextMenu) => {
      log.debug('[TreeStore] Setting context menu:', () => ({ menu }));
      set({ contextMenu: menu });
    },

    clearContextMenu: () => {
      log.debug('[TreeStore] Clearing context menu');
      set({ contextMenu: null });
    },

    clearTree: () => {
      log.debug('[TreeStore] Clearing tree and entity context');
      const authStore = useAuthStore.getState();
      authStore.clearEntityPrivileges();
      set({
        // Node metadata
        nodes: new Map<string, TreeNode>(),

        // Entity data
        data: new Map<string, any>(),
        files: new Map<string, FileMetaDataDto[]>(),
        notes: new Map<string, NoteDto[]>(),
        sampleRequests: new Map<number, SampleRequestDto[]>(),

        // Loading state
        notesLoaded: new Set<string>(),
        filesLoaded: new Set<string>(),
        childrenLoaded: new Set<string>(),

        // Modification state
        modifiedData: new Set<string>(),

        // UI state
        expandedNodes: new Set<string>(),
        selectedNode: null,
        loadedNodes: new Set<string>(),
        contextMenu: null,
      });
    },

    // Data management (new)
    setData: (dataId: string, data: NodeDataType) => {
      log.debug('[TreeStore] Setting data:', { dataId });
      set((state) => {
        const newData = new Map(state.data);
        newData.set(dataId, data);
        return { data: newData };
      });
    },

    setFiles: (dataId: string, files: FileMetaDataDto[]) => {
      log.debug('[TreeStore] Setting files:', { dataId, count: files.length });
      set((state) => {
        const newFiles = new Map(state.files);
        newFiles.set(dataId, files);
        return { files: newFiles };
      });
    },

    /** Appends new file metadata to the existing files for a dataId (e.g. after upload). */
    addFiles: (dataId: string, newFiles: FileMetaDataDto[]) => {
      if (newFiles.length === 0) return;
      log.debug('[TreeStore] Adding files:', { dataId, count: newFiles.length });
      set((state) => {
        const newFilesMap = new Map(state.files);
        const current = newFilesMap.get(dataId) ?? [];
        newFilesMap.set(dataId, [...current, ...newFiles]);
        return { files: newFilesMap };
      });
    },

    /** Replaces an existing file metadata by id (e.g. after update). */
    replaceFile: (dataId: string, updatedFile: FileMetaDataDto) => {
      const metadataId = updatedFile.id;
      if (metadataId == null) return;
      log.debug('[TreeStore] Replacing file:', { dataId, metadataId });
      set((state) => {
        const newFilesMap = new Map(state.files);
        const current = newFilesMap.get(dataId) ?? [];
        const index = current.findIndex((f) => f.id === metadataId);
        if (index < 0) return state;
        const updated = [...current];
        updated[index] = updatedFile;
        newFilesMap.set(dataId, updated);
        return { files: newFilesMap };
      });
    },

    /** Removes file metadata by id (e.g. after delete). */
    removeFile: (dataId: string, metadataId: number) => {
      log.debug('[TreeStore] Removing file:', { dataId, metadataId });
      set((state) => {
        const newFilesMap = new Map(state.files);
        const current = newFilesMap.get(dataId) ?? [];
        const filtered = current.filter((f) => f.id !== metadataId);
        if (filtered.length === current.length) return state;
        newFilesMap.set(dataId, filtered);
        return { files: newFilesMap };
      });
    },

    setNotes: (dataId: string, notes: NoteDto[]) => {
      log.debug('[TreeStore] Setting notes:', { dataId, count: notes.length });
      set((state) => {
        const newNotes = new Map(state.notes);
        newNotes.set(dataId, notes);
        return { notes: newNotes };
      });
    },

    setSampleRequests: (projectId: number, requests: SampleRequestDto[]) => {
      log.debug('[TreeStore] Setting sample requests:', { projectId, count: requests.length });
      set((state) => {
        const newSampleRequests = new Map(state.sampleRequests);
        newSampleRequests.set(projectId, requests);
        return { sampleRequests: newSampleRequests };
      });
    },

    getSampleRequests: (projectId: number): SampleRequestDto[] | undefined => {
      return get().sampleRequests.get(projectId);
    },

    // Loading flag management (new)
    setNotesLoaded: (dataId: string) => {
      log.debug('[TreeStore] Setting notes loaded:', { dataId });
      set((state) => {
        const newNotesLoaded = new Set(state.notesLoaded);
        newNotesLoaded.add(dataId);
        return { notesLoaded: newNotesLoaded };
      });
    },

    setFilesLoaded: (dataId: string) => {
      log.debug('[TreeStore] Setting files loaded:', { dataId });
      set((state) => {
        const newFilesLoaded = new Set(state.filesLoaded);
        newFilesLoaded.add(dataId);
        return { filesLoaded: newFilesLoaded };
      });
    },

    setChildrenLoaded: (nodeId: string) => {
      log.debug('[TreeStore] Setting children loaded:', { nodeId });
      set((state) => {
        const newChildrenLoaded = new Set(state.childrenLoaded);
        newChildrenLoaded.add(nodeId);
        return { childrenLoaded: newChildrenLoaded };
      });
    },

    clearChildrenLoaded: (nodeId: string) => {
      log.debug('[TreeStore] Clearing children loaded:', { nodeId });
      set((state) => {
        const newChildrenLoaded = new Set(state.childrenLoaded);
        newChildrenLoaded.delete(nodeId);
        return { childrenLoaded: newChildrenLoaded };
      });
    },

    invalidateChildrenCache: (nodeId: string) => {
      TreeNodeFactory.invalidateChildrenCache(nodeId);
    },

    // Modification state management (new)
    setDataModified: (dataId: string, isModified: boolean) => {
      log.debug('[TreeStore] Setting data modified state:', { dataId, isModified });
      set((state) => {
        const newModifiedData = new Set(state.modifiedData);
        if (isModified) {
          newModifiedData.add(dataId);
        } else {
          newModifiedData.delete(dataId);
        }
        return { modifiedData: newModifiedData };
      });
    },

    loadChildren: async (nodeId: string, backend: IBackend) => {
      const { nodes, setNode, setLoadedNode, setLoading, loading } = get();
      const node = nodes.get(nodeId);
      if (!node) {
        log.error('[TreeStore] Node not found:', { nodeId });
        return;
      }
      if (loading.get(`load-${nodeId}`)) {
        // Don't start it again.
        return;
      }
      try {
        setLoading(`load-${nodeId}`, true);
        const { factory } = get();
        if (!factory) throw new Error('Factory not initialized');
        // Get child descriptors instead of created nodes to apply data protection logic
        const descriptors = await factory.loadChildrenDescriptors(node, backend, get().getData);
        log.debug('[TreeStore] loadChildren - Got descriptors:', { nodeId, count: descriptors.length, descriptors });

        // Create all child nodes in batch
        const childNodeDescriptors = descriptors.map((descriptor) => ({
          type: descriptor.type,
          data: descriptor.data,
          parentId: nodeId,
          needsDataReload: descriptor.isFullyLoaded === false,
        }));

        const childNodeIds = await get().createNodes(childNodeDescriptors);
        log.debug('[TreeStore] loadChildren - Created child nodes:', { nodeId, childNodeIds });

        // Sort the children using the factory's sort method
        const childNodes = childNodeIds.map((id: string) => get().nodes.get(id)).filter(Boolean) as TreeNode[];
        const sortedChildren = factory.sort(childNodes, get().getData);
        const sortedChildIds = sortedChildren.map((child) => child.nodeId);

        setNode({
          ...node,
          children: sortedChildIds,
          isExpandable: sortedChildIds.length > 0, // Runtime state based on actual children
        });
        get().setChildrenLoaded(nodeId);
        setLoadedNode(nodeId);
      } catch (error) {
        log.error('[TreeStore] Error loading children:', error);
        throw error;
      } finally {
        setLoading(`load-${nodeId}`, false);
      }
    },

    updateData: async (nodeId: string, data: NodeDataType) => {
      const { setData, setDataModified } = get();
      const node = resolveNode(nodeId);
      if (!node) {
        log.error('[TreeStore] Node not found:', { nodeId });
        return;
      }

      const currentData = get().data.get(node.dataId) || {};
      const changedFields = computeChangedFields(
        currentData as Record<string, unknown>,
        data as Record<string, unknown>
      );

      // Update the data in the store (complete replacement)
      setData(node.dataId, data);
      setDataModified(node.dataId, true);
      syncTreeAfterDataChange(node, currentData, changedFields);

      logStoreState('updateData', { nodeId, data });
    },

    updateDataPartial: async (nodeId: string, partialData: Partial<NodeDataType>) => {
      const { setData, setDataModified } = get();
      const node = resolveNode(nodeId);
      if (!node) {
        log.error('[TreeStore] Node not found:', { nodeId });
        return;
      }

      // Get current data and merge with new data
      const currentData = get().data.get(node.dataId) || {};
      const updatedData = { ...currentData, ...partialData };

      // Update the data in the store
      setData(node.dataId, updatedData);
      setDataModified(node.dataId, true);
      syncTreeAfterDataChange(node, currentData, partialData as Record<string, unknown>);

      logStoreState('updateDataPartial', { nodeId, partialData });
    },

    mergeEntityFromServer: (nodeId: string, partialData: Partial<NodeDataType>) => {
      const { nodes, setData } = get();
      const node = nodes.get(nodeId);
      if (!node) {
        log.error('[TreeStore] mergeEntityFromServer: node not found:', { nodeId });
        return;
      }

      const currentData = get().data.get(node.dataId) || {};
      const updatedData = { ...currentData, ...partialData };
      setData(node.dataId, updatedData);

      logStoreState('mergeEntityFromServer', { nodeId, partialData });
    },

    /** Apply a full post-save server DTO without treating it as a new user edit. */
    replaceEntityFromServer: (nodeId: string, data: NodeDataType) => {
      const { setData, setDataModified } = get();
      const node = resolveNode(nodeId);
      if (!node) {
        log.error('[TreeStore] replaceEntityFromServer: node not found:', { nodeId });
        return;
      }

      const currentData = get().data.get(node.dataId) || {};
      const changedFields = computeChangedFields(
        currentData as Record<string, unknown>,
        data as Record<string, unknown>
      );

      setData(node.dataId, data);
      setDataModified(node.dataId, false);
      syncTreeAfterDataChange(node, currentData, changedFields);

      logStoreState('replaceEntityFromServer', { nodeId });
    },

    deleteNode: async (nodeId: string) => {
      set((state) => {
        const newNodes = new Map(state.nodes);
        const nodeToDelete = newNodes.get(nodeId);
        if (nodeToDelete?.parentId) {
          const parent = newNodes.get(nodeToDelete.parentId);
          if (parent) {
            const updatedChildren = parent.children?.filter((childId) => childId !== nodeId) || [];
            newNodes.set(parent.nodeId, { ...parent, children: updatedChildren });
          }
        }
        newNodes.delete(nodeId);
        return { nodes: newNodes };
      });
      return Promise.resolve();
    },

    loadNotes: async (dataId: string, backend: IBackend) => {
      if (get().isNotesLoaded(dataId)) {
        return;
      }
      const { nodes, setNotes, setNotesLoaded } = get();

      // Find a node with this dataId to get the node context
      const nodeWithData = Array.from(nodes.values()).find((node) => node.dataId === dataId);
      if (!nodeWithData) {
        log.error('[TreeStore] No node found with dataId:', { dataId });
        return;
      }

      const { factory } = get();
      if (!factory) throw new Error('Factory not initialized');

      const notes = await factory.loadNotes(nodeWithData, backend);

      // Update notes and loading state in the store
      setNotes(dataId, notes);
      setNotesLoaded(dataId);

      log.debug('[TreeStore] Notes loaded:', { dataId, count: notes.length });
    },

    loadFiles: async (dataId: string, backend: IBackend) => {
      // Always refetch when invoked — callers (e.g. after upload/delete or snapshot refresh) need fresh file PKs.
      // Callers that want to skip redundant loads should guard with isFilesLoaded before calling.
      const { nodes, setFiles, setFilesLoaded, setLoading } = get();

      // Find a node with this dataId to get the node context
      const nodeWithData = Array.from(nodes.values()).find((node) => node.dataId === dataId);
      if (!nodeWithData) {
        log.error('[TreeStore] No node found with dataId:', { dataId });
        return;
      }

      setLoading(`files-${dataId}`, true);

      try {
        const { factory } = get();
        if (!factory) throw new Error('Factory not initialized');
        const files = await factory.loadFiles(nodeWithData, backend);

        // Update files and loading state in the store
        setFiles(dataId, files);
        setFilesLoaded(dataId);

        log.debug('[TreeStore] Files loaded:', { dataId, count: files.length });
      } catch (error) {
        log.error('[TreeStore] Error loading files:', {
          dataId,
          error: formatErrorMessage(error),
        });

        // Set empty files array and mark as loaded on error
        setFiles(dataId, []);
        setFilesLoaded(dataId);
      } finally {
        setLoading(`files-${dataId}`, false);
      }
    },

    /**
     * Retrieves a node's data from the store
     * @param nodeId - ID of the node to retrieve
     * @returns The node data or null if not found
     */
    getNodeData: (nodeId: string) => {
      return get().nodes.get(nodeId) || null;
    },

    /**
     * Gets the display name for a node
     * @param type - Type of the node
     * @param nodeId - ID of the node
     * @returns The node's label or a fallback display name
     */
    getNodeDisplayName: (type: NodeType, nodeId: string) => {
      const node = get().nodes.get(nodeId);
      return node?.label || `${type} ${nodeId}`;
    },

    // Data access (legacy - for factory compatibility)
    getData: (dataId: string) => {
      return get().data.get(dataId) || null;
    },

    // Type-safe data access with automatic type inference - eliminates need for manual casting
    getTypedData: <T extends NodeType>(node: TreeNode<T>): NodeDataTypeMap[T] | null => {
      const data = get().data.get(node.dataId);
      if (!data) return null;

      // Type assertion is safe here because we know the node type matches the data type
      return data as NodeDataTypeMap[T];
    },

    // Type-safe data access helpers - eliminates need for manual casting
    getSampleData: (node: TreeNode): SampleDto | null => {
      if (
        node.type !== 'sample' &&
        node.type !== 'sampleAggregate' &&
        node.type !== 'sampleSnapshot' &&
        node.type !== 'sampleInPackage'
      )
        return null;
      return (get().data.get(node.dataId) as SampleDto) || null;
    },

    getProjectData: (node: TreeNode): ProjectUIModel | null => {
      if (node.type !== 'project' && node.type !== 'projectInData') return null;
      return (get().data.get(node.dataId) as ProjectUIModel) || null;
    },

    getSampleActivityData: (node: TreeNode): SampleActivityDto | null => {
      if (
        node.type !== 'sampleActivity' &&
        node.type !== 'sampleActivityAggregate' &&
        node.type !== 'sampleActivityInPackage' &&
        node.type !== 'sampleActivitySnapshot'
      )
        return null;
      return (get().data.get(node.dataId) as SampleActivityDto) || null;
    },

    getProjectActivityData: (node: TreeNode): ProjectActivityDto | null => {
      if (node.type !== 'projectActivity') return null;
      return (get().data.get(node.dataId) as ProjectActivityDto) || null;
    },

    getTheoryActivityData: (node: TreeNode): TheoryActivityDto | null => {
      if (node.type !== 'theoryActivity' && node.type !== 'theoryActivityInPackage') return null;
      return (get().data.get(node.dataId) as TheoryActivityDto) || null;
    },

    getDataPackageData: (node: TreeNode): DataPackageDto | null => {
      if (node.type !== 'dataPackage' && node.type !== 'simulationDataset') return null;
      return (get().data.get(node.dataId) as DataPackageDto) || null;
    },

    getCommunicationData: (node: TreeNode): CommunicationDto | null => {
      if (node.type !== 'communication') return null;
      return (get().data.get(node.dataId) as CommunicationDto) || null;
    },

    getShippingData: (node: TreeNode): ShippingDto | null => {
      if (node.type !== 'shipping') return null;
      return (get().data.get(node.dataId) as ShippingDto) || null;
    },

    getPublicationData: (node: TreeNode): PublicationDto | null => {
      if (node.type !== 'publication' && node.type !== 'publicationInData') return null;
      return (get().data.get(node.dataId) as PublicationDto) || null;
    },

    getFiles: (dataId: string) => {
      return get().files.get(dataId) || [];
    },

    getNotes: (dataId: string) => {
      return get().notes.get(dataId) || [];
    },

    getSelectedData: () => {
      const { selectedNode, data } = get();
      if (!selectedNode) return null;
      return data.get(selectedNode.dataId) || null;
    },

    getSelectedFiles: () => {
      const { selectedNode, files } = get();
      if (!selectedNode) return [];
      return files.get(selectedNode.dataId) || [];
    },

    getSelectedNotes: () => {
      const { selectedNode, notes } = get();
      if (!selectedNode) return [];
      return notes.get(selectedNode.dataId) || [];
    },

    // Loading state checks (new)
    isNotesLoaded: (dataId: string) => {
      return get().notesLoaded.has(dataId);
    },

    isFilesLoaded: (dataId: string) => {
      return get().filesLoaded.has(dataId);
    },

    areChildrenLoaded: (nodeId: string) => {
      return get().childrenLoaded.has(nodeId);
    },

    // Modification state checks (new)
    isDataModified: (dataId: string) => {
      return get().modifiedData.has(dataId);
    },

    hasModifiedDataInSubtree: (nodeId: string) => {
      const { nodes, modifiedData } = get();
      const node = nodes.get(nodeId);
      if (!node) return false;
      if (modifiedData.has(node.dataId)) return true;
      for (const childId of node.children ?? []) {
        if (get().hasModifiedDataInSubtree(childId)) return true;
      }
      return false;
    },

    expandNode: (nodeId: string) => {
      log.debug('[TreeStore] Expanding node:', { nodeId });
      set((state) => {
        const newExpanded = new Set(state.expandedNodes);
        newExpanded.add(nodeId);
        return { expandedNodes: newExpanded };
      });
    },

    collapseNode: (nodeId: string) => {
      log.debug('[TreeStore] Collapsing node:', { nodeId });
      set((state) => {
        const newExpanded = new Set(state.expandedNodes);
        newExpanded.delete(nodeId);
        return { expandedNodes: newExpanded };
      });
    },

    selectNode: (nodeId: string) => {
      log.debug('[TreeStore] Selecting node:', { nodeId });
      set((state) => {
        const node = nodeId ? state.nodes.get(nodeId) : null;
        if (!node) {
          log.debug('[TreeStore] Node not found during selection:', { nodeId });
          return { selectedNode: null };
        }
        return { selectedNode: node };
      });
    },

    reloadNode: async (nodeId: string, backend: IBackend) => {
      const { nodes, setNode, setLoading } = get();
      const originalNode = nodes.get(nodeId);
      if (!originalNode) {
        log.error('[TreeStore] Node not found:', { nodeId });
        return;
      }
      const config = TreeNodeFactory.getNodeConfig(originalNode.type);
      try {
        setLoading(`reload-${nodeId}`, true);
        const api = createTreeStoreBackendApi(backend);
        // Extract entity ID from composite dataId using helper method
        const entityId = extractEntityIdFromDataId(originalNode.dataId);
        const freshData = await api.getNode(originalNode.type, entityId);

        // Update the data in the store
        get().setData(originalNode.dataId, freshData);

        // If reloading a project, clear sampleRequests cache to force reload
        if (originalNode.type === 'project' || originalNode.type === 'projectInData') {
          const projectId = (freshData as ProjectUIModel)?.id;
          if (projectId) {
            set((state) => {
              const newSampleRequests = new Map(state.sampleRequests);
              newSampleRequests.delete(projectId);
              log.debug('[TreeStore] Cleared sampleRequests cache for reloaded project:', { projectId });
              return { sampleRequests: newSampleRequests };
            });
          }
        }

        // Load notes and files if the config supports it (clear cached flags so reload always refetches)
        set((state) => {
          const notesLoaded = new Set(state.notesLoaded);
          notesLoaded.delete(originalNode.dataId);
          const filesLoaded = new Set(state.filesLoaded);
          filesLoaded.delete(originalNode.dataId);
          return { notesLoaded, filesLoaded };
        });
        if (config.loadNotes) {
          await get().loadNotes(originalNode.dataId, backend);
        }
        if (config.loadFiles) {
          await get().loadFiles(originalNode.dataId, backend);
        }

        const display = TreeNodeFactory.getNodeDisplayData(originalNode.type, freshData);
        const latestNode = get().nodes.get(nodeId);
        if (!latestNode) {
          log.error('[TreeStore] Node removed during reload:', { nodeId });
          return;
        }
        setNode(
          TreeNodeFactory.withNodeDisplayData(
            {
              ...latestNode,
              needsDataReload: false, // Node data is now fully loaded
              dataVersion: (latestNode.dataVersion || 0) + 1, // Increment to trigger component reloads
            },
            display
          )
        );
      } catch (error) {
        log.error('[TreeStore] Error reloading node:', error);
        throw error;
      } finally {
        setLoading(`reload-${nodeId}`, false);
      }
    },

    /**
     * Reloads only the entity data for a node (no notes, no files).
     * Use when you've updated files/notes in the store and only need fresh entity data
     * (e.g. after file upload, when entity metadata might have changed).
     */
    reloadNodeData: async (nodeId: string, backend: IBackend) => {
      const { nodes, setNode, setLoading } = get();
      const originalNode = nodes.get(nodeId);
      if (!originalNode) {
        log.error('[TreeStore] Node not found:', { nodeId });
        return;
      }
      try {
        setLoading(`reload-${nodeId}`, true);
        const api = createTreeStoreBackendApi(backend);
        const entityId = extractEntityIdFromDataId(originalNode.dataId);
        const freshData = await api.getNode(originalNode.type, entityId);

        get().setData(originalNode.dataId, freshData);

        if (originalNode.type === 'project' || originalNode.type === 'projectInData') {
          const projectId = (freshData as ProjectUIModel)?.id;
          if (projectId) {
            set((state) => {
              const newSampleRequests = new Map(state.sampleRequests);
              newSampleRequests.delete(projectId);
              return { sampleRequests: newSampleRequests };
            });
          }
        }

        const display = TreeNodeFactory.getNodeDisplayData(originalNode.type, freshData);
        const latestNode = get().nodes.get(nodeId);
        if (!latestNode) return;
        setNode(
          TreeNodeFactory.withNodeDisplayData(
            {
              ...latestNode,
              needsDataReload: false,
              dataVersion: (latestNode.dataVersion || 0) + 1,
            },
            display
          )
        );
      } catch (error) {
        log.error('[TreeStore] Error reloading node data:', error);
        throw error;
      } finally {
        setLoading(`reload-${nodeId}`, false);
      }
    },

    createNode: async (type: NodeType, parentId: string | undefined, data: NodeDataType) => {
      log.debug('[TreeStore] createNode called:', { type, parentId, dataId: data?.id });
      const { nodes, setNode, factory, setLoading } = get();
      const parent = parentId ? nodes.get(parentId) : null;
      if (parentId && !parent) {
        throw new Error(`Parent node not found: ${parentId}`);
      }
      try {
        setLoading(`create-${type}`, true);
        if (!factory) throw new Error('Factory not initialized');

        // Get existing node IDs to ensure uniqueness
        const existingNodeIds = new Set(nodes.keys());
        log.debug('[TreeStore] createNode - Before factory call:', {
          type,
          parentId,
          existingNodeIds: Array.from(existingNodeIds),
          dataId: data?.id,
        });
        const newNode = factory.createNode(type, data, parentId, existingNodeIds);
        log.debug('[TreeStore] createNode - After factory call:', {
          nodeId: newNode.nodeId,
          dataId: newNode.dataId,
          type: newNode.type,
        });

        // Store the data separately, but protect modified data
        const { data: existingData, modifiedData } = get();
        const hasExistingData = existingData.has(newNode.dataId);
        const isDataModified = modifiedData.has(newNode.dataId);

        log.debug('[TreeStore] createNode - Data protection check:', {
          nodeId: newNode.nodeId,
          dataId: newNode.dataId,
          hasExistingData,
          isDataModified,
          existingDataKeys: Array.from(existingData.keys()),
          modifiedDataKeys: Array.from(modifiedData),
        });

        if (!hasExistingData) {
          // No existing data, safe to store new data
          get().setData(newNode.dataId, data);
          log.debug('[TreeStore] Stored new data for node:', {
            nodeId: newNode.nodeId,
            dataId: newNode.dataId,
          });
        } else if (!isDataModified) {
          // Data exists but not modified, safe to overwrite with fresher data
          get().setData(newNode.dataId, data);
          log.debug('[TreeStore] Updated existing data with fresh data:', {
            nodeId: newNode.nodeId,
            dataId: newNode.dataId,
          });
        } else {
          // Data is modified but not saved, preserve existing data
          log.warn('[TreeStore] Preserving modified data, not overwriting:', {
            nodeId: newNode.nodeId,
            dataId: newNode.dataId,
            message: 'Existing data is modified but not saved',
          });
        }

        log.debug('[TreeStore] createNode - About to call setNode:', {
          nodeId: newNode.nodeId,
          dataId: newNode.dataId,
        });
        setNode(newNode);
        log.debug('[TreeStore] createNode - After setNode call');

        // Add the new node to parent's children and sort them (if parent exists)
        if (parent) {
          const updatedChildIds = [...(parent.children || []), newNode.nodeId];
          // Get all child nodes for sorting
          const childNodes = updatedChildIds.map((id) => get().nodes.get(id)).filter(Boolean) as TreeNode[];
          const sortedChildren = factory.sort(childNodes, get().getData);
          const sortedChildIds = sortedChildren.map((child) => child.nodeId);

          setNode({
            ...parent,
            children: sortedChildIds,
            isExpandable: sortedChildIds.length > 0,
          });
        }

        log.debug('[TreeStore] createNode - Returning nodeId:', newNode.nodeId);
        return newNode.nodeId;
      } catch (error) {
        log.error('[TreeStore] Error creating node:', error);
        throw error;
      } finally {
        setLoading(`create-${type}`, false);
      }
    },

    createNodes: async (
      descriptors: Array<{ type: NodeType; data: NodeDataType; parentId?: string; needsDataReload?: boolean }>
    ) => {
      log.debug('[TreeStore] createNodes called:', { count: descriptors.length });
      const { nodes, factory, setLoading } = get();

      if (!factory) throw new Error('Factory not initialized');

      try {
        setLoading('create-batch', true);

        // Get existing node IDs to ensure uniqueness
        const existingNodeIds = new Set(nodes.keys());
        const newNodes: TreeNode[] = [];
        const dataUpdates = new Map<string, NodeDataType>();

        // Create all nodes without updating the store yet
        for (const descriptor of descriptors) {
          const { type, data, parentId, needsDataReload } = descriptor;
          const parent = parentId ? nodes.get(parentId) : null;
          if (parentId && !parent) {
            throw new Error(`Parent node not found: ${parentId}`);
          }

          log.debug('[TreeStore] createNodes - Creating node:', { type, parentId, dataId: data?.id });
          const newNode = factory.createNode(type, data, parentId, existingNodeIds);

          // Mark as needing data reload if specified
          if (needsDataReload) {
            newNode.needsDataReload = true;
          }

          newNodes.push(newNode);
          existingNodeIds.add(newNode.nodeId);

          // Prepare data updates with protection logic
          const { data: existingData, modifiedData } = get();
          const hasExistingData = existingData.has(newNode.dataId);
          const isDataModified = modifiedData.has(newNode.dataId);

          if (!hasExistingData) {
            // No existing data, safe to store new data
            dataUpdates.set(newNode.dataId, data);
            log.debug('[TreeStore] Will store new data for node:', {
              nodeId: newNode.nodeId,
              dataId: newNode.dataId,
            });
          } else if (!isDataModified) {
            // Data exists but not modified, safe to overwrite with fresher data
            dataUpdates.set(newNode.dataId, data);
            log.debug('[TreeStore] Will update existing data with fresh data:', {
              nodeId: newNode.nodeId,
              dataId: newNode.dataId,
            });
          } else {
            // Data is modified but not saved, preserve existing data
            log.warn('[TreeStore] Preserving modified data, not overwriting:', {
              nodeId: newNode.nodeId,
              dataId: newNode.dataId,
              message: 'Existing data is modified but not saved',
            });
          }
        }

        // Batch update: first update all data, then all nodes
        set((state) => {
          const newData = new Map(state.data);
          for (const [dataId, data] of dataUpdates) {
            newData.set(dataId, data);
          }
          return { data: newData };
        });

        set((state) => {
          const newNodesMap = new Map(state.nodes);
          for (const node of newNodes) {
            // Always recalculate label to ensure we don't miss changes
            const nodeData = state.data.get(node.dataId);
            const display =
              state.factory && nodeData
                ? TreeNodeFactory.getNodeDisplayData(node.type, nodeData, state.factory.userSettings)
                : TreeNodeFactory.getNodeDisplayData(node.type, nodeData || {});
            newNodesMap.set(node.nodeId, TreeNodeFactory.withNodeDisplayData(node, display));
          }
          return { nodes: newNodesMap };
        });

        log.debug('[TreeStore] createNodes - Created nodes:', {
          count: newNodes.length,
          nodeIds: newNodes.map((n) => n.nodeId),
        });
        return newNodes.map((n) => n.nodeId);
      } catch (error) {
        log.error('[TreeStore] Error creating nodes:', error);
        throw error;
      } finally {
        setLoading('create-batch', false);
      }
    },

    partiallyLoadChildren: async (
      nodeId: string,
      childPredicate: (childData: NodeDataType) => boolean,
      backend: IBackend
    ) => {
      const { nodes, setNode, setLoadedNode, setLoading } = get();
      const node = nodes.get(nodeId);
      if (!node) {
        log.error('[TreeStore] Node not found:', { nodeId });
        return;
      }

      try {
        setLoading(`load-specific-child-${nodeId}`, true);
        const { factory } = get();
        if (!factory) throw new Error('Factory not initialized');
        // Get child descriptors instead of created nodes to apply data protection logic
        const descriptors = await factory.loadChildrenDescriptors(node, backend, get().getData);
        log.debug('[TreeStore] partiallyLoadChildren - Got descriptors:', {
          nodeId,
          count: descriptors.length,
          descriptors,
        });

        // Filter descriptors by predicate
        const filteredDescriptors = descriptors.filter((descriptor) => childPredicate(descriptor.data));
        log.debug('[TreeStore] partiallyLoadChildren - Filtered descriptors:', {
          nodeId,
          filteredCount: filteredDescriptors.length,
        });

        // Create all filtered child nodes in batch
        const childNodeDescriptors = filteredDescriptors.map((descriptor) => ({
          type: descriptor.type,
          data: descriptor.data,
          parentId: nodeId,
          needsDataReload: descriptor.isFullyLoaded === false,
        }));

        const childNodeIds = await get().createNodes(childNodeDescriptors);
        log.debug('[TreeStore] partiallyLoadChildren - Created child nodes:', { nodeId, childNodeIds });

        // Get all child nodes for sorting
        const childNodes = childNodeIds.map((id: string) => get().nodes.get(id)).filter(Boolean) as TreeNode[];

        // Sort the children using the factory's sort method
        const sortedChildren = factory.sort(childNodes, get().getData);
        const sortedChildIds = sortedChildren.map((child) => child.nodeId);

        // Update parent node with children references and runtime isExpandable
        setNode({
          ...node,
          children: sortedChildIds,
          isExpandable: sortedChildIds.length > 0, // Runtime state based on actual children
        });

        // Mark children as loaded if all descriptors were processed
        if (filteredDescriptors.length === descriptors.length) {
          get().setChildrenLoaded(nodeId);
        }
        setLoadedNode(nodeId);

        return childNodes;
      } catch (error) {
        log.error('[TreeStore] Error loading specific child:', error);
        throw error;
      } finally {
        setLoading(`load-specific-child-${nodeId}`, false);
      }
    },

    initializeTree: async (
      rootType: NodeType,
      rootId: string,
      userSettings: UserSettingsDto,
      preloadedRoot?: NodeDataType
    ): Promise<string> => {
      const { setNode, setLoadedNode, expandNode, setSelectedNode, setFactory, setLoading } = get();

      const canonicalDataBrowse = Boolean(
        (userSettings as Record<string, unknown> | undefined)?.[DATA_ROUTE_ROOT_MODE_KEY]
      );
      const storeRootType = applyCanonicalDataBrowseRootNodeMapping(rootType, canonicalDataBrowse);

      const rootNodeId = `${generateNodeId(storeRootType, rootId)}`;
      const loadingKey = `init-${rootNodeId}`;
      try {
        log.debug('[TreeStore] Initializing tree:', { rootType, storeRootType, rootId, userSettings });
        setLoading(loadingKey, true);

        setFactory(userSettings);

        const backend = useBackendStore.getState().getBackend();
        if (!backend) {
          throw new Error('Backend not initialized');
        }

        const api = createTreeStoreBackendApi(backend);
        const root = await loadTreeRootData(storeRootType, rootType, rootId, preloadedRoot, api);
        if (!root) {
          throw new Error(`Failed to load ${storeRootType} with ID ${rootId}`);
        }
        const { factory } = get();
        if (!factory) throw new Error('Factory not initialized');

        // Get existing node IDs to ensure uniqueness (should be empty for root)
        const existingNodeIds = new Set(get().nodes.keys());
        const rootNode = factory.createNode(storeRootType, root, undefined, existingNodeIds);

        // /data route roots may use DOI or prePublishId; factory createNodeId defaults to numeric data.id.
        const routeRootDataId = generateNodeId(storeRootType, rootId);
        if (rootType === 'dataPackage' && rootNode.dataId !== routeRootDataId) {
          rootNode.dataId = routeRootDataId;
          rootNode.nodeId = routeRootDataId;
        }

        // Store the root data separately, but protect modified data
        const { data: existingData, modifiedData } = get();
        const hasExistingData = existingData.has(rootNode.dataId);
        const isDataModified = modifiedData.has(rootNode.dataId);

        if (!hasExistingData) {
          // No existing data, safe to store new data
          get().setData(rootNode.dataId, root);
          log.debug('[TreeStore] Stored new root data:', {
            nodeId: rootNode.nodeId,
            dataId: rootNode.dataId,
          });
        } else if (!isDataModified) {
          // Data exists but not modified, safe to overwrite with fresher data
          get().setData(rootNode.dataId, root);
          log.debug('[TreeStore] Updated existing root data with fresh data:', {
            nodeId: rootNode.nodeId,
            dataId: rootNode.dataId,
          });
        } else {
          // Data is modified but not saved, preserve existing data
          log.warn('[TreeStore] Preserving modified root data, not overwriting:', {
            nodeId: rootNode.nodeId,
            dataId: rootNode.dataId,
            message: 'Existing root data is modified but not saved',
          });
        }

        setNode(rootNode);
        setLoadedNode(rootNode.nodeId);
        expandNode(rootNode.nodeId);
        setSelectedNode(rootNode.nodeId);

        // Set entity context based on root type
        const authStore = useAuthStore.getState();
        if (rootType === 'project' || rootType === 'sample') {
          log.debug('[TreeStore] Setting entity context:', { rootType, rootId });
          authStore.setEntityContext(rootId, rootType);
        } else {
          // Clear entity context for other tree types
          // This ensures privilege checks fall back to default JWT privileges only
          log.debug('[TreeStore] Clearing entity context for non-entity tree type:', { rootType });
          authStore.clearEntityPrivileges();
        }

        await get().loadChildren(rootNode.nodeId, backend);
        return rootNode.nodeId;
      } catch (error) {
        log.error('[TreeStore] Error initializing tree:', { rootType, rootId, error });
        throw error;
      } finally {
        setLoading(loadingKey, false);
      }
    },
  };
});
